import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import AdminLayout from '../layouts/AdminLayout';
import { Link, useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { BsCurrencyDollar, BsBagCheck, BsBoxSeam, BsPeople, BsHourglassSplit, BsCheckCircle, BsTruck, BsHouseDoor } from 'react-icons/bs';
import { useTheme } from '../context/ThemeContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Dashboard = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const navigate = useNavigate();
    const { darkMode } = useTheme();
    const user = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user'));

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await axios.get('/dashboard');
                setStats(res.data);
            } catch (error) {
                console.error('İstatistikler yüklenemedi');
            }
        };
        fetchStats();
    }, []);

    if (!stats) {
        return (
            <AdminLayout>
                <div className="p-4">{t('common.loading')}</div>
            </AdminLayout>
        );
    }

    const handleStatusClick = (status) => {
        const target = stats.role === 'admin' ? '/admin/orders' : '/my-orders';
        navigate(target, { state: { status } });
    };

    // Grafik Ayarları (Dark Mode Uyumu)
    const chartTextColor = darkMode ? '#e9ecef' : '#495057';
    const chartGridColor = darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';

    const barOptions = {
        responsive: true,
        plugins: {
            legend: { labels: { color: chartTextColor } },
            title: { display: false }
        },
        scales: {
            x: {
                ticks: { color: chartTextColor },
                grid: { display: false }
            },
            y: {
                ticks: { color: chartTextColor },
                grid: { color: chartGridColor }
            }
        }
    };

    const doughnutOptions = {
        responsive: true,
        plugins: {
            legend: { labels: { color: chartTextColor } }
        },
        elements: {
            arc: {
                borderColor: darkMode ? '#212529' : '#fff' // Kart arka planına uygun border
            }
        }
    };

    // Grafik Verileri
    const salesChartData = {
        labels: stats.monthlySales?.map(item => item.month) || [],
        datasets: [
            {
                label: t('dashboard.sales_chart'),
                data: stats.monthlySales?.map(item => item.total) || [],
                backgroundColor: 'rgba(23, 165, 178, 0.5)',
            },
        ],
    };

    const doughnutData = {
        labels: [t('dashboard.approved'), t('my_orders.status_pending'), t('my_orders.status_cancelled')],
        datasets: [
            {
                data: [stats.totalOrders, 2, 1], // Örnek veri
                backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
                borderWidth: 1,
            },
        ],
    };

    return (
        <AdminLayout>
            <h2 className="fw-bold mb-4">{t('dashboard.welcome')}, {user?.username}</h2>

            <div className="row mt-4">
                {/* KARTLAR */}
                <div className="col-sm-6 col-lg-3 mb-4">
                    <div className="card dashboard-stat-card h-100 border-0 shadow-sm">
                        <div className="card-body d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted mb-2">{stats.role === 'admin' ? t('dashboard.total_revenue') : t('dashboard.total_expense')}</h6>
                                <h3 className="fw-bold mb-0 text-primary">
                                    {Number(stats.totalRevenue).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}
                                </h3>
                            </div>
                            <div className="icon-box bg-primary-soft rounded-circle">
                                <BsCurrencyDollar size={24} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-sm-6 col-lg-3 mb-4">
                    <div className="card dashboard-stat-card h-100 border-0 shadow-sm">
                        <div className="card-body d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted mb-2">{t('dashboard.total_orders')}</h6>
                                <h3 className="fw-bold mb-0 text-success">{stats.totalOrders}</h3>
                            </div>
                            <div className="icon-box bg-success-soft rounded-circle">
                                <BsBagCheck size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {stats.role === 'admin' && (
                    <>
                        <div className="col-sm-6 col-lg-3 mb-4">
                            <div className="card dashboard-stat-card h-100 border-0 shadow-sm">
                                <div className="card-body d-flex align-items-center justify-content-between">
                                    <div>
                                        <h6 className="text-muted mb-2">{t('dashboard.total_products')}</h6>
                                        <h3 className="fw-bold mb-0 text-warning">{stats.totalProducts}</h3>
                                    </div>
                                    <div className="icon-box bg-warning-soft rounded-circle">
                                        <BsBoxSeam size={24} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6 col-lg-3 mb-4">
                            <div className="card dashboard-stat-card h-100 border-0 shadow-sm">
                                <div className="card-body d-flex align-items-center justify-content-between">
                                    <div>
                                        <h6 className="text-muted mb-2">{t('dashboard.total_dealers')}</h6>
                                        <h3 className="fw-bold mb-0 text-info">{stats.totalDealers}</h3>
                                    </div>
                                    <div className="icon-box bg-info-soft rounded-circle">
                                        <BsPeople size={24} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* SİPARİŞ DURUMLARI */}
            <h5 className="fw-bold mb-3">{t('dashboard.order_statuses')}</h5>
            <div className="row mb-4">
                <div className="col-md-3 col-6 mb-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-stat-card" style={{cursor: 'pointer'}} onClick={() => handleStatusClick('pending')}>
                        <div className="card-body text-center p-3">
                            <div className="icon-box bg-warning-soft rounded-circle mx-auto mb-2">
                                <BsHourglassSplit size={20} />
                            </div>
                            <h6 className="text-muted small mb-1">{t('dashboard.new_order')}</h6>
                            <h4 className="fw-bold mb-0">{stats.statusCounts?.pending || 0}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-6 mb-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-stat-card" style={{cursor: 'pointer'}} onClick={() => handleStatusClick('approved')}>
                        <div className="card-body text-center p-3">
                            <div className="icon-box bg-info-soft rounded-circle mx-auto mb-2">
                                <BsCheckCircle size={20} />
                            </div>
                            <h6 className="text-muted small mb-1">{t('dashboard.approved')}</h6>
                            <h4 className="fw-bold mb-0">{stats.statusCounts?.approved || 0}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-6 mb-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-stat-card" style={{cursor: 'pointer'}} onClick={() => handleStatusClick('preparing')}>
                        <div className="card-body text-center p-3">
                            <div className="icon-box bg-primary-soft rounded-circle mx-auto mb-2">
                                <BsBoxSeam size={20} />
                            </div>
                            <h6 className="text-muted small mb-1">{t('dashboard.preparing')}</h6>
                            <h4 className="fw-bold mb-0">{stats.statusCounts?.preparing || 0}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-6 mb-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-stat-card" style={{cursor: 'pointer'}} onClick={() => handleStatusClick('shipped')}>
                        <div className="card-body text-center p-3">
                            <div className="icon-box bg-secondary rounded-circle mx-auto mb-2 text-white" style={{backgroundColor: '#6c757d'}}>
                                <BsTruck size={20} />
                            </div>
                            <h6 className="text-muted small mb-1">{t('dashboard.shipped')}</h6>
                            <h4 className="fw-bold mb-0">{stats.statusCounts?.shipped || 0}</h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* GRAFİKLER VE LİSTELER */}
            <div className="row mb-4">
                <div className="col-md-8 mb-4">
                    <div className="card p-3 h-100">
                        <h5 className="card-title mb-3">{t('dashboard.sales_chart')}</h5>
                        <Bar data={salesChartData} options={barOptions} />
                    </div>
                </div>
                <div className="col-md-4 mb-4">
                    <div className="card p-3 h-100">
                        <h5 className="card-title mb-3">{t('dashboard.order_status_chart')}</h5>
                        <div className="d-flex justify-content-center"><div style={{width: '80%'}}><Doughnut data={doughnutData} options={doughnutOptions} /></div></div>
                    </div>
                </div>
            </div>

            {/* ADMIN ÖZEL: ÇOK SATANLAR VE EN İYİ BAYİLER */}
            {stats.role === 'admin' && (
                <div className="row mb-4">
                    <div className="col-md-6 mb-4">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-transparent border-bottom py-3">
                                <h6 className="m-0 fw-bold">{t('dashboard.best_sellers')}</h6>
                            </div>
                            <ul className="list-group list-group-flush">
                                {stats.bestSellers?.map((item, index) => (
                                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                        {item.name}
                                        <span className="badge bg-primary rounded-pill">{item.total_sold} {t('common.unit_piece')}</span>
                                    </li>
                                ))}
                                {(!stats.bestSellers || stats.bestSellers.length === 0) && <li className="list-group-item text-muted">{t('dashboard.no_data')}</li>}
                            </ul>
                        </div>
                    </div>
                    <div className="col-md-6 mb-4">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-transparent border-bottom py-3">
                                <h6 className="m-0 fw-bold">{t('dashboard.top_dealers')}</h6>
                            </div>
                            <ul className="list-group list-group-flush">
                                {stats.topDealers?.map((item, index) => (
                                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="fw-bold">{item.company_name || item.username}</div>
                                            <small className="text-muted">{item.order_count} {t('dealer_detail.order_count')}</small>
                                        </div>
                                        <span className="text-success fw-bold">{Number(item.total_spent).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</span>
                                    </li>
                                ))}
                                {(!stats.topDealers || stats.topDealers.length === 0) && <li className="list-group-item text-muted">{t('dashboard.no_data')}</li>}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* SON SİPARİŞLER TABLOSU */}
            <div className="card shadow-sm mt-4">
                <div className="card-header bg-transparent border-bottom py-3">
                    <h5 className="m-0 fw-bold">{t('dashboard.recent_orders')}</h5>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>{t('dashboard.order_no')}</th>
                                <th>{t('dashboard.date')}</th>
                                <th>{t('dashboard.amount')}</th>
                                <th>{t('dashboard.status')}</th>
                                <th>{t('dashboard.detail')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.recentOrders?.map(order => (
                                <tr key={order.id}>
                                    <td>{order.order_number}</td>
                                    <td>{new Date(order.created_at).toLocaleDateString('tr-TR')}</td>
                                    <td>{Number(order.final_amount).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</td>
                                    <td>
                                        <span className={`badge bg-${
                                            order.status === 'delivered' ? 'success' : 
                                            order.status === 'cancelled' ? 'danger' : 'warning'
                                        } text-dark`}>
                                            {order.status}
                                        </span>
                                    </td>
                                    <td>
                                        <Link to={`/orders/${order.id}`} className="btn btn-sm btn-outline-primary">{t('dashboard.inspect')}</Link>
                                    </td>
                                </tr>
                            ))}
                            {stats.recentOrders?.length === 0 && (
                                <tr><td colSpan="5" className="text-center p-3">{t('dashboard.no_orders')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
};

export default Dashboard;
